import ky from 'ky'
import React from 'react'
import useSWRImmutable from 'swr/immutable'

import Header from '@/components/bills-tracker/header'
import Loading from '@/components/bills-tracker/loading'
import FeatureBlock from '@/components/feature-block'
import type {InvoicesApprovalsQuery} from '@/graphql-types'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section} from '@helios/shared/styles/grid'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

import type {InvoiceStatus} from './invoices-approvals'

interface InvoicesApprovalsProps {
    invoiceId: string | null
    approverId: string | null
    token: string | null
    action: InvoiceStatus | null
    content: PrismicData<InvoicesApprovalsQuery, 'allPrismicInvoicesApprovals'>['data']
}

type InvoiceApproval = {
    id: string
    approverEmployeeId: string
    requesterEmployeeId: string
    expenseType: 'BILL_INVOICE'
    expenseId: string
    status: InvoiceStatus
    requestMeta: {
        companyName: string
        supplierName: string
    }
    createdAt: string
    updatedAt: string
}

const InvoicesApprovalsTemplate = ({
    invoiceId,
    approverId,
    token,
    action,
    content,
}: InvoicesApprovalsProps) => {
    const {data: invoiceApprovals, error} = useSWRImmutable<InvoiceApproval, Error>(
        `${process.env.GATSBY_INVOICES_APPROVAL_BASE_URL}/approvals/BILL-INVOICE/${invoiceId}/requests/${approverId}/status`,
        (url: string) =>
            ky
                .put(url, {
                    json: {
                        status: action,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                .json<InvoiceApproval>()
    )

    const getBodyContent = (text: string) => {
        return replacePlaceholders(text, {
            '{{supplier_name}}': invoiceApprovals?.requestMeta.supplierName,
        }).join('')
    }

    const landingPageContent = {
        APPROVED: {
            title: content.approvalSuccess[0].title,
            body: getBodyContent(content.approvalSuccess[0].approval_success_text.richText[0].text),
            image: content.approvalSuccess[0].image,
        },
        REJECTED: {
            title: content.rejectionSuccess[0].title,
            body: getBodyContent(
                content.rejectionSuccess[0].rejection_success_text.richText[0].text
            ),
            image: content.rejectionSuccess[0].image,
        },
        ERROR: {
            title: content.errorProcessingRequest[0].title,
            body: content.errorProcessingRequest[0].error_processing_request_text.richText,
            image: content.errorProcessingRequest[0].image,
        },
    }

    const getPageContent = () => {
        if (error || !action) {
            return landingPageContent.ERROR
        }
        return landingPageContent[action]
    }

    return (
        <>
            <Header
                ctaText={content.headerCta}
                ctaLink={content.headerCtaLink}
                headerLink={content.headerLink.richText}
            />

            <Main>
                <Section padding="small">
                    {!invoiceApprovals && !error ? (
                        <Loading />
                    ) : (
                        <FeatureBlock {...getPageContent()}>
                            <Button as={PrismicLink} {...content.pageCtaLink}>
                                {content.pageCtaText}
                            </Button>
                        </FeatureBlock>
                    )}
                </Section>
            </Main>
        </>
    )
}

export default InvoicesApprovalsTemplate
